import { setSession } from "./components/account/auth-helper";
import ServerDataBaseHandler from "./persistence/SeverDataBaseHandler";
import universalstorageHandler from "./persistence/UniversalstorageHandler";
import axios from "./utils/axios";

import Logger from "./Logger";
import ZipStorageHandler from "./persistence/ZipStorageHandler";
import VersionManagement from "./persistence/VersionManagement";
import ObjectFactory from "./models/ObjectFactory";
import BaseModel from "./models/BaseModel";
import BaseModelMap from "./models/BaseModelMap";
import ProjectMetadata from "./models/ProjectMetadata";
import { cleanEncode } from "./utils/CommonUtils";

const logger = new Logger();

export default class SyncManager {
  projectMetadata: ProjectMetadata;

  constructor(projectMetadata: ProjectMetadata) {
    this.projectMetadata = projectMetadata;
  }

  private itemsToBeMerged = new Map<string, any>();

  private loadData = async (id: string | undefined, parentId: string | undefined): Promise<any> => {
    if (id) {
      const item = this.itemsToBeMerged.get(id);
      if (item) {
        VersionManagement.updateItemFromOldVersion(this.projectMetadata.version, item);
      } else {
        logger.error("item==null", id);
      }
      return item;
    } else if (parentId) {
      const res: any = [];

      this.itemsToBeMerged.forEach((item) => {
        if (item?.parentId == parentId) {
          res.push(item);
        }
      });

      return res;
    }
  };

  private async loadAndMergeIncomingItems(): Promise<boolean> {
    try {
      logger.log("loadAndMergeIncomingItems");

      const response = await axios.get(`/api/project/itemsasblob/${cleanEncode(this.projectMetadata.id)}`, {
        responseType: "blob",
      });

      logger.log("loadIncomingItems");

      if (response.status == 200) {
        const z = new ZipStorageHandler();
        const blob = response.data;
        const isValidZip = await z.setBlob(blob);
        if (isValidZip) {
          //
          // find all items that should be loaded into the current state
          //
          z.items.forEach((item) => {
            VersionManagement.updateItemFromOldVersion(this.projectMetadata.version, item);
            if (this.incomingItemIsNewer(item)) {
              logger.log("Incoming Item found", item);
              this.itemsToBeMerged.set(item.id, item);
            }
          });

          //
          // patch the properies of all items that are in the current state
          //
          this.itemsToBeMerged.forEach((incomingItem) => {
            const currentObject = state.objectRegistry.getObject(incomingItem.id) as BaseModel;
            if (currentObject) {
              //
              // check if the incoming item has a different parentId because it is moved
              const incommingParentCollection = state.objectRegistry.getObject(
                incomingItem.parentId,
              ) as BaseModelMap<BaseModel>;
              if (incommingParentCollection.className == "BaseModelMap") {
                if (currentObject.parent?.id != incommingParentCollection.id) {
                  //
                  // we have to move to object to a new parent
                  //
                  const oldParentCollection = state.objectRegistry.getParentCollectionOfObject(currentObject);
                  if (incommingParentCollection.parent) {
                    currentObject.moveToNewParent(incommingParentCollection.parent as BaseModel);
                  } else {
                    logger.error("incommingParentCollection.paren== undefined");
                  }
                }
              }

              //
              // merge the properties of the "incomingItem" to the "currentObject"
              //
              const currentModel = currentObject as BaseModel;
              const data = { ...incomingItem };
              delete data.id;
              delete data.parentId;
              currentModel.fromPersistentData(data);
              currentModel.raiseOnChange("@sync");
            }
          });

          //
          // load all items that are not current state
          //

          this.itemsToBeMerged.forEach((item) => {
            const currentItem = state.objectRegistry.getObject(item.id);
            if (!currentItem) {
              const parentCollection = state.objectRegistry.getObject(item.parentId) as BaseModelMap<BaseModel>;
              if (parentCollection) {
                const parent = parentCollection.parent as BaseModel;
                if (parent) {
                  const newItem = ObjectFactory.createObject(item.className, {}, parent);
                  if (newItem) {
                    newItem.loadFromEstadaStorage(item, undefined, this.loadData);
                    parentCollection.add(newItem);
                    state.objectRegistry.registerObject(newItem);
                  }
                } else {
                  logger.error("parent == undefined");
                }
              }
            }
          });

          //
          // delete all items that are flages as incomming deleted
          //
          const deletedIds = z.items.get("deletedIds"); // no itx
          if (deletedIds) {
            logger.log("execute incomming deletes", deletedIds);
            deletedIds.forEach((id: string) => {
              const obj = state.objectRegistry.getObject(id);
              if (obj) {
                logger.log("incomming delete: id found, delete it ", id, obj.className, obj.tag);
                obj.delete();
              } else {
                logger.log("incomming delete: id not found ", id);
              }
            });
          }
        }
      }
      return true;
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  private incomingItemIsNewer(incomingItem: any): boolean {
    if (!incomingItem.className) {
      return false;
    }

    const currentObject = state.objectRegistry.getObject(incomingItem.id);

    // Option 1:
    //  Items id is NOT known in the current project
    //

    if (!currentObject) {
      return true;
    }

    // Option 2:
    //  currentObject.modifiedTs is older then the incomingItem.modifiedTs
    //

    if (currentObject.modifiedTs == incomingItem.modifiedTs) {
      return false;
    }

    if (currentObject.modifiedTs < incomingItem.modifiedTs) {
      console.log("incomming item is newer: " + incomingItem.name);
      return true;
    } else {
      console.log("incomming item is older: " + incomingItem.name);
      return false;
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////

  async mergeIncomingVersionFromServer(): Promise<boolean> {
    try {
      const serverDataBaseHandler = new ServerDataBaseHandler();
      const res = await serverDataBaseHandler.login(universalstorageHandler.creditals);
      if (res?.token) {
        setSession(res.token);
        state.isLoggedIn = true;
        return await this.loadAndMergeIncomingItems();
      }
    } catch (ex) {
      console.error(ex);
    }

    return false;
  }
}
